import React, { FC, useMemo, useEffect, useState, useRef } from 'react';
import InjurieslimitationsMaleNeckIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-neck.webp';
import InjurieslimitationsMaleShouldersIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-shoulders.webp';
import InjurieslimitationsMaleChestIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-chest.webp';
import InjurieslimitationsMaleCoreIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-core.webp';
import InjurieslimitationsMaleBackIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-back.webp';
import InjurieslimitationsMaleWristsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-wrists.webp';
import InjurieslimitationsMaleElbowIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-elbow.webp';
import InjurieslimitationsMaleArmsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-arms.webp';
import InjurieslimitationsMaleHipsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-hips.webp';
import InjurieslimitationsMaleKneesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-knees.webp';
import InjurieslimitationsMaleAnklesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-ankles.webp';
import InjurieslimitationsMaleGlutesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-male-glutes.webp';
import InjurieslimitationsFemaleNeckIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-neck.webp';
import InjurieslimitationsFemaleShouldersIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-shoulders.webp';
import InjurieslimitationsFemaleChestIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-chest.webp';
import InjurieslimitationsFemaleCoreIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-core.webp';
import InjurieslimitationsFemaleBackIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-back.webp';
import InjurieslimitationsFemaleWristsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-wrists.webp';
import InjurieslimitationsFemaleElbowIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-elbow.webp';
import InjurieslimitationsFemaleArmsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-arms.webp';
import InjurieslimitationsFemaleHipsIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-hips.webp';
import InjurieslimitationsFemaleKneesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-knees.webp';
import InjurieslimitationsFemaleAnklesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-ankles.webp';
import InjurieslimitationsFemaleGlutesIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-glutes.webp';
import InjurieslimitationsFemalePregnancyIcon from 'url:src/assets/images/trainer/program-generator/injurieslimitations-female-pregnancy.webp';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { resolveBodyProfile, isFemaleProfile } from 'src/utils/genderUtils';
import './InjuriesLimitationsStep.scss';

interface InjuriesLimitationsStepProps {
  clientName?: string;
  bodyProfile?: string;
  selectedParts: string[];
  onChangeParts: (parts: string[]) => void;
  notes: string;
  onChangeNotes: (notes: string) => void;
}

// Any injuries or limitations to keep in mind?
export const InjuriesLimitationsStep: FC<InjuriesLimitationsStepProps> = ({
  bodyProfile = 'male',
  selectedParts,
  onChangeParts,
  notes,
  onChangeNotes,
}) => {
  const [localNote, setLocalNote] = useState(notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalNote(notes || '');
  }, [notes]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleNotesChange = (val: string) => {
    setIsSaved(false);
    setLocalNote(val);
  };

  const handleAddNote = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!localNote.trim() || !/\p{L}/u.test(localNote) || isSaving) return;

    setIsSaving(true);
    setIsSaved(false);
    const trimmed = localNote.trim();
    setLocalNote(trimmed);
    onChangeNotes(trimmed);

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(false);
      setIsSaved(true);
    }, 500);
  };
  const genderKey = resolveBodyProfile(bodyProfile);
  const isFemale = isFemaleProfile(bodyProfile);
  const iconMapping: Record<string, React.ReactNode> = {
    neck: isFemale ? (
      <img
        src={InjurieslimitationsFemaleNeckIcon}
        alt="InjurieslimitationsFemaleNeckIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleNeckIcon}
        alt="InjurieslimitationsMaleNeckIcon"
        className="program-svg-icon"
      />
    ),
    shoulders: isFemale ? (
      <img
        src={InjurieslimitationsFemaleShouldersIcon}
        alt="InjurieslimitationsFemaleShouldersIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleShouldersIcon}
        alt="InjurieslimitationsMaleShouldersIcon"
        className="program-svg-icon"
      />
    ),
    chest: isFemale ? (
      <img
        src={InjurieslimitationsFemaleChestIcon}
        alt="InjurieslimitationsFemaleChestIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleChestIcon}
        alt="InjurieslimitationsMaleChestIcon"
        className="program-svg-icon"
      />
    ),
    core: isFemale ? (
      <img
        src={InjurieslimitationsFemaleCoreIcon}
        alt="InjurieslimitationsFemaleCoreIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleCoreIcon}
        alt="InjurieslimitationsMaleCoreIcon"
        className="program-svg-icon"
      />
    ),
    back: isFemale ? (
      <img
        src={InjurieslimitationsFemaleBackIcon}
        alt="InjurieslimitationsFemaleBackIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleBackIcon}
        alt="InjurieslimitationsMaleBackIcon"
        className="program-svg-icon"
      />
    ),
    wrists: isFemale ? (
      <img
        src={InjurieslimitationsFemaleWristsIcon}
        alt="InjurieslimitationsFemaleWristsIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleWristsIcon}
        alt="InjurieslimitationsMaleWristsIcon"
        className="program-svg-icon"
      />
    ),
    elbow: isFemale ? (
      <img
        src={InjurieslimitationsFemaleElbowIcon}
        alt="InjurieslimitationsFemaleElbowIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleElbowIcon}
        alt="InjurieslimitationsMaleElbowIcon"
        className="program-svg-icon"
      />
    ),
    arms: isFemale ? (
      <img
        src={InjurieslimitationsFemaleArmsIcon}
        alt="InjurieslimitationsFemaleArmsIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleArmsIcon}
        alt="InjurieslimitationsMaleArmsIcon"
        className="program-svg-icon"
      />
    ),
    hips: isFemale ? (
      <img
        src={InjurieslimitationsFemaleHipsIcon}
        alt="InjurieslimitationsFemaleHipsIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleHipsIcon}
        alt="InjurieslimitationsMaleHipsIcon"
        className="program-svg-icon"
      />
    ),
    knees: isFemale ? (
      <img
        src={InjurieslimitationsFemaleKneesIcon}
        alt="InjurieslimitationsFemaleKneesIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleKneesIcon}
        alt="InjurieslimitationsMaleKneesIcon"
        className="program-svg-icon"
      />
    ),
    ankles: isFemale ? (
      <img
        src={InjurieslimitationsFemaleAnklesIcon}
        alt="InjurieslimitationsFemaleAnklesIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleAnklesIcon}
        alt="InjurieslimitationsMaleAnklesIcon"
        className="program-svg-icon"
      />
    ),
    glutes: isFemale ? (
      <img
        src={InjurieslimitationsFemaleGlutesIcon}
        alt="InjurieslimitationsFemaleGlutesIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={InjurieslimitationsMaleGlutesIcon}
        alt="InjurieslimitationsMaleGlutesIcon"
        className="program-svg-icon"
      />
    ),
    pregnancy: isFemale ? (
      <img
        src={InjurieslimitationsFemalePregnancyIcon}
        alt="InjurieslimitationsFemalePregnancyIcon"
        className="program-svg-icon"
      />
    ) : null,
  };
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const injuriesLimitationsData = programMasterData?.injuriesLimitations;

  const togglePart = (id: string) => {
    onChangeParts(
      selectedParts.includes(id) ? selectedParts.filter((p) => p !== id) : [...selectedParts, id],
    );
  };

  const injuriesOptions = injuriesLimitationsData?.options;
  const options = useMemo(() => {
    return [...(injuriesOptions || [])]
      .filter((opt: any) => !opt.targetProfile || opt.targetProfile === genderKey)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [injuriesOptions, genderKey]);

  useEffect(() => {
    if (options.length > 0 && selectedParts.length > 0) {
      const allowedSlugs = new Set(options.map((opt: any) => opt.slug));
      const filtered = selectedParts.filter((p) => allowedSlugs.has(p));
      if (filtered.length !== selectedParts.length) {
        onChangeParts(filtered);
      }
    }
  }, [options, selectedParts, onChangeParts]);

  const { title, subtitle, note } = injuriesLimitationsData || {};

  if (isLoading) {
    return <div className="injuries-limitations-step">{'Loading options...'}</div>;
  }

  if (isError || !injuriesLimitationsData) {
    return <div className="injuries-limitations-step">{'Error loading options.'}</div>;
  }

  const isAddDisabled = !localNote.trim() || !/\p{L}/u.test(localNote) || isSaving;

  return (
    <div className="injuries-limitations-step">
      <h2 className="step-title">{title}</h2>
      <p className="step-subtitle">{subtitle}</p>

      <div className="body-parts-grid">
        {options.map((part) => (
          <div
            key={part.id}
            className={`body-part-card ${selectedParts.includes(part.slug) ? 'selected' : ''}`}
            onClick={() => togglePart(part.slug)}
          >
            <div className="part-image">{iconMapping[part.id]}</div>
            <span className="part-label">{part.title}</span>
          </div>
        ))}
      </div>

      <div className="notes-section">
        <label className="notes-label">{note?.title}</label>
        <textarea
          className={`notes-input ${localNote.trim() !== '' && !/\p{L}/u.test(localNote) ? 'error-border' : ''}`}
          placeholder={note?.placeholder}
          value={localNote}
          maxLength={1000}
          onChange={(e) => handleNotesChange(e.target.value)}
          onBlur={(e) => handleNotesChange(e.target.value.trim())}
        />
        {localNote.trim() !== '' && !/\p{L}/u.test(localNote) && (
          <p className="error-message">Notes must contain at least one valid letter.</p>
        )}
        <small
          style={{
            display: 'block',
            textAlign: 'right',
            color: '#a0a0ab',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {localNote.length}/1000
        </small>
        <div className="mobile-add-btn">
          <button
            type="button"
            className={`add-btn ${isSaving ? 'saving' : ''}`}
            disabled={isAddDisabled}
            onClick={handleAddNote}
          >
            {isSaving ? (
              <>
                <span className="btn-spinner" />
                <span>Saving...</span>
              </>
            ) : (
              'Add'
            )}
          </button>
          {isSaved && (
            <div className="note-saved-status">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3334 4L6.00008 11.3333L2.66675 8"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Note saved</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
